/**
 * SloEditor.tsx — SLO definition create/edit form
 *
 * Purpose:    Inline form for creating or editing an SLO definition (name,
 *             metric query, target %, and rolling window).
 * Inputs:     initial?: Partial<SloDefinition>, onSave, onCancel
 * Outputs:    Validated form; calls onSave with sanitized SLO data
 * Constraints: Target must be 0–100; metric query is free-form PromQL string
 */
import { useState } from 'react'
import type { SloDefinition, SloWindow } from '../../hooks/useSloData'

const WINDOWS: SloWindow[] = ['7d', '30d', '90d']

export function SloEditor({
  initial,
  onSave,
  onCancel,
}: {
  initial?: Partial<SloDefinition>
  onSave: (s: Omit<SloDefinition, 'id' | 'createdAt'> & { id?: string }) => Promise<boolean>
  onCancel: () => void
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [metricQuery, setMetricQuery] = useState(initial?.metricQuery ?? '')
  const [targetPercent, setTargetPercent] = useState(String(initial?.targetPercent ?? 99.9))
  const [window, setWindow] = useState<SloWindow>(initial?.window ?? '30d')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    const target = Number(targetPercent)
    if (isNaN(target) || target < 0 || target > 100) {
      setError('Target must be 0–100')
      return
    }
    if (!name.trim() || !metricQuery.trim()) {
      setError('Name and metric query are required')
      return
    }
    setSaving(true)
    await onSave({ id: initial?.id, name: name.trim(), metricQuery: metricQuery.trim(), targetPercent: target, window })
    setSaving(false)
    onCancel()
  }

  return (
    <form onSubmit={(e) => { void handleSave(e) }} className="rounded-lg border border-sky-200 dark:border-sky-800/50 bg-brand-surface/80 p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-brand-muted">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="API availability"
            className="w-full rounded border border-brand-card bg-brand-surface-2 px-3 py-1.5 text-sm text-brand-ink placeholder-gray-500 focus:border-sky-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-brand-muted">Window</label>
          <select
            value={window}
            onChange={(e) => setWindow(e.target.value as SloWindow)}
            className="w-full rounded border border-brand-card bg-brand-surface-2 px-2 py-1.5 text-sm text-brand-ink focus:border-sky-500 focus:outline-none"
          >
            {WINDOWS.map((w) => (
              <option key={w} value={w}>{w}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs text-brand-muted">Metric query</label>
          <input
            type="text"
            value={metricQuery}
            onChange={(e) => setMetricQuery(e.target.value)}
            placeholder='sum(rate(http_requests_total{status!~"5.."}[5m])) / sum(rate(http_requests_total[5m]))'
            className="w-full rounded border border-brand-card bg-brand-surface-2 px-3 py-1.5 text-sm text-brand-ink placeholder-gray-500 focus:border-sky-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-brand-muted">Target (%)</label>
          <input
            type="number"
            step="0.001"
            min={0}
            max={100}
            value={targetPercent}
            onChange={(e) => setTargetPercent(e.target.value)}
            className="w-full rounded border border-brand-card bg-brand-surface-2 px-3 py-1.5 text-sm text-brand-ink focus:border-sky-500 focus:outline-none"
          />
        </div>
      </div>
      {error && <p className="mt-2 text-xs text-red-700 dark:text-red-400">{error}</p>}
      <div className="mt-3 flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-sky-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save SLO'}
        </button>
        <button type="button" onClick={onCancel} className="rounded-md border border-brand-card px-4 py-1.5 text-sm text-brand-secondary hover:bg-brand-surface-2">
          Cancel
        </button>
      </div>
    </form>
  )
}
